import { useState } from "react"
import { Link } from "react-router-dom"
import { Menu, X, Globe, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import logo from "@/assets/logo.png"

const languages = [
  { code: "en-BW", label: "English (Botswana)", flag: "🇧🇼" },
  { code: "en-ZA", label: "English (South Africa)", flag: "🇿🇦" },
  { code: "en-KE", label: "English (Kenya)", flag: "🇰🇪" },
  { code: "en-TZ", label: "English (Tanzania)", flag: "🇹🇿" },
  { code: "en-ZW", label: "English (Zimbabwe)", flag: "🇿🇼" },
  { code: "en-NA", label: "English (Namibia)", flag: "🇳🇦" },
  { code: "sw-TZ", label: "Swahili (Tanzania)", flag: "🇹🇿" },
  { code: "zu-ZA", label: "Zulu (South Africa)", flag: "🇿🇦" },
]

const serviceCategories = [
  { href: "/categories/accommodation", label: "Accommodation" },
  { href: "/categories/safari", label: "Safaris & Adventures" },
  { href: "/categories/dining", label: "Dining & Nightlife" },
  { href: "/categories/wellness", label: "Wellness & Beauty" },
  { href: "/categories/transport", label: "Car Hire & Transport" },
]

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [currentLang, setCurrentLang] = useState(languages[0])

  const handleLanguageChange = (lang: (typeof languages)[0]) => {
    setCurrentLang(lang)
    // Language switching can be handled here if needed
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#75055B]/98 backdrop-blur-md border-b border-[#DB4789]/20 shadow-lg overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[#DB4789]/10 via-transparent to-[#DB4789]/10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex items-center justify-between h-28 md:h-36 lg:h-40">
          {/* Left Navigation - Desktop */}
          <div className="hidden lg:flex items-center space-x-8 flex-1">
            <Link to="/" className="text-sm font-semibold tracking-wide uppercase text-white hover:text-[#DB4789] transition-all duration-300 relative group">
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#DB4789] transition-all duration-300 group-hover:w-full"></span>
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-sm font-semibold tracking-wide uppercase text-white hover:text-[#DB4789] transition-all duration-300 flex items-center gap-1 group">
                  Explore Services
                  <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:rotate-180" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-white/95 backdrop-blur-md border border-gray-200/50 shadow-xl rounded-xl">
                {serviceCategories.map((category) => (
                  <DropdownMenuItem
                    key={category.href}
                    asChild
                    className="text-gray-900 hover:bg-[#DB4789]/10 focus:bg-[#DB4789]/10 focus:text-[#75055B] transition-colors"
                  >
                    <Link to={category.href} className="cursor-pointer">
                      {category.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/categories" className="text-sm font-semibold tracking-wide uppercase text-white hover:text-[#DB4789] transition-all duration-300 relative group">
              Categories
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#DB4789] transition-all duration-300 group-hover:w-full"></span>
            </Link>
          </div>

          {/* Center Logo - Reduced by 50% */}
          <div className="absolute left-1/2 transform -translate-x-1/2 mt-4 md:mt-6 lg:mt-8">
            <Link to="/" className="flex items-center justify-center group">
              <img
                src={logo}
                alt="StayApp - Your Stay, Your Way"
                className="h-32 md:h-40 lg:h-48 w-auto transition-all duration-300 group-hover:scale-110 drop-shadow-2xl"
              />
            </Link>
          </div>

          {/* Right Navigation & CTA - Desktop */}
          <div className="hidden lg:flex items-center space-x-4 flex-1 justify-end">
            <Link to="/providers" className="text-sm font-semibold tracking-wide uppercase text-white hover:text-[#DB4789] transition-all duration-300 relative group">
              For Providers
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#DB4789] transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link to="/support" className="text-sm font-semibold tracking-wide uppercase text-white hover:text-[#DB4789] transition-all duration-300 relative group">
              Support
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#DB4789] transition-all duration-300 group-hover:w-full"></span>
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-white hover:text-white/80 hover:bg-white/10">
                  <Globe className="h-4 w-4 mr-2" />
                  <span className="text-xl mr-1">{currentLang.flag}</span>
                  {currentLang.code.split("-")[0].toUpperCase()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang)}
                    className="cursor-pointer"
                  >
                    <span className="text-xl mr-2">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="sm" className="text-white hover:text-white/80 hover:bg-white/10 font-semibold tracking-wide uppercase">
              Login
            </Button>

            <Button className="bg-white text-[#75055B] hover:bg-[#DB4789] hover:text-white hover:shadow-xl hover:shadow-[#DB4789]/30 transition-all duration-300 font-semibold tracking-wide uppercase px-6 rounded-full">
              Get Started
            </Button>
          </div>

          {/* Mobile: Logo on left, Menu button on right */}
          <div className="lg:hidden flex items-center justify-between w-full">
            <Link to="/" className="flex items-center group">
              <img
                src={logo}
                alt="StayApp - Your Stay, Your Way"
                className="h-20 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-white hover:text-white/80 transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-white/20">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-sm font-semibold tracking-wide uppercase text-white hover:text-[#DB4789] transition-colors px-2 py-1"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>

              <div className="px-2">
                <p className="text-xs font-semibold text-white/70 mb-2">Explore Services</p>
                <div className="flex flex-col space-y-2 pl-2">
                  {serviceCategories.map((category) => (
                    <Link
                      key={category.href}
                      to={category.href}
                      className="text-sm text-white/90 hover:text-[#DB4789] transition-colors py-1"
                      onClick={() => setIsOpen(false)}
                    >
                      {category.label}
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/categories"
                className="text-sm font-semibold tracking-wide uppercase text-white hover:text-[#DB4789] transition-colors px-2 py-1"
                onClick={() => setIsOpen(false)}
              >
                Categories
              </Link>

              <Link
                to="/providers"
                className="text-sm font-semibold tracking-wide uppercase text-white hover:text-[#DB4789] transition-colors px-2 py-1"
                onClick={() => setIsOpen(false)}
              >
                For Providers
              </Link>

              <Link
                to="/support"
                className="text-sm font-semibold tracking-wide uppercase text-white hover:text-[#DB4789] transition-colors px-2 py-1"
                onClick={() => setIsOpen(false)}
              >
                Support
              </Link>

              <div className="px-2 py-2 border-t border-white/20">
                <p className="text-xs font-semibold text-white/70 mb-2">Language & Country</p>
                <select
                  value={currentLang.code}
                  onChange={(e) => {
                    const lang = languages.find((l) => l.code === e.target.value)
                    if (lang) handleLanguageChange(lang)
                  }}
                  className="w-full px-3 py-2 border border-white/30 rounded-lg text-sm bg-white/10 text-white"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code} className="text-gray-900">
                      {lang.flag} {lang.label}
                    </option>
                  ))}
                </select>
              </div>

              <Button variant="outline" className="w-full bg-white/10 text-white border-white/30 hover:bg-white/20">
                Login
              </Button>

              <Button className="bg-white text-[#75055B] hover:bg-[#DB4789] hover:text-white hover:shadow-lg transition-all w-full font-medium">
                Get Started
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
